import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import _ from "lodash";
import { sendCommand } from "../modules/playEngineBridge";
import * as previewQueue from "../modules/playEnginePreviewQueue";
import GameFlowDiagram from "./GameFlowDiagram";
import PlayEngineTabSearch, { matchesTabSearch } from "./playEngineTabSearch";

// ─── Field hints ──────────────────────────────────────────────────────────────
//
// Add an entry here for fields that need a Select dropdown (enums).
// Everything else is inferred automatically from the runtime value type:
//   boolean → Switch
//   number  → number TextField
//   string  → text TextField  (or Select if listed here)
//   object  → sub-section rendered recursively
//
// ADD new entries when you add new enum fields to GeneralSettings in the
// PlayEngine — other field types require NO dashboard changes.
//
const FIELD_HINTS = {
  masterVolume: {
    label: "Master Volume",
    type: "slider",
    min: 0,
    max: 1,
    step: 0.01,
  },
  sipPlayable: {
    label: "Single Interaction Playable (SIP)",
  },
  // Canvas block (playEngine >= 0.1.32). Nested object -> the renderer already
  // walks into it; these only give the section and the toggle better names than
  // the camelCase fallback ("Canva" / "Debug").
  canva: {
    label: "Canvas",
  },
  baseDimensions: {
    label: "Base Dimensions (design space)",
  },
  debug: {
    label: "Show canvas bounds (preview only)",
  },
};

/**
 * Rendered by a section of their own further down, so the generic tree must skip
 * them — otherwise the same fields would appear twice.
 * 
 */
const OWN_SECTION_FIELDS = new Set(["canva"]);


const HIDDEN_FIELDS = new Set([
  "soundEnabled",
  "playableEndType",
  "endPlayableAfterSeconds",
  "endPlayableAfterTaps",
]);

// Backfill for snapshots saved before a field existed. Only reached on the
// FALLBACK path (preview iframe not ready, settings read straight off the
// persisted version) — when the engine answers, it has already merged its own
// defaults in RestoreModule, so this list is a second line of defence, not the
// source of truth.
const GENERAL_SETTINGS_DEFAULTS = {
  sipPlayable: false,
  canva: {
    baseDimensions: {
      portrait: { width: 375, height: 667 },
      landscape: { width: 667, height: 375 },
    },
    debug: false,
  },
};

function withGeneralSettingsDefaults(settings) {
  if (!settings || typeof settings !== "object") return settings;
  return { ...GENERAL_SETTINGS_DEFAULTS, ...settings };
}

// Human-readable labels derived from camelCase keys when no hint is provided.
function labelFromKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

const CARD_BG = "#474747";
const FIELD_BG = "#313131";
// Debounce window for version/DB persist. Local UI always updates immediately.
const PERSIST_DEBOUNCE_MS = 200;

function isContinuousPreview(path, newValue) {
  if (typeof newValue === "boolean") return false;
  const leafKey = path[path.length - 1];
  if (FIELD_HINTS[leafKey]?.type === "select") return false;
  return true;
}

// ─── Dynamic field renderer ───────────────────────────────────────────────────
/**
 * Renders a single settings field (or sub-section) based on its runtime type.
 *
 * @param {string}   fieldKey    Leaf key name
 * @param {*}        value       Current value
 * @param {string[]} path        Full dot-path from root settings object
 * @param {function} onChange    Called with (path, newValue)
 */
function settingMatches(key, value, search) {
  if (!search.trim() || matchesTabSearch(key, search) || matchesTabSearch(FIELD_HINTS[key]?.label ?? labelFromKey(key), search)) return true;
  return value !== null && typeof value === "object" && !Array.isArray(value) &&
    Object.entries(value).some(([childKey, childValue]) => settingMatches(childKey, childValue, search));
}

function SettingsField({ fieldKey, value, path, onChange, search = "" }) {
  const hint = FIELD_HINTS[fieldKey];
  const label = hint?.label ?? labelFromKey(fieldKey);
  if (!settingMatches(fieldKey, value, search) && !matchesTabSearch(label, search)) return null;
  const childSearch = matchesTabSearch(fieldKey, search) || matchesTabSearch(label, search) ? "" : search;

  // ── Nested object → sub-section ──────────────────────────────────────────
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    return (
      <div style={{ marginBottom: "12px" }}>
        <Typography
          variant="caption"
          style={{ color: "#aaa", display: "block", marginBottom: "6px", fontWeight: 600 }}
        >
          {label}
        </Typography>
        <div style={{ paddingLeft: "12px", borderLeft: "2px solid #555" }}>
          {Object.entries(value).map(([k, v]) => (
            <SettingsField
              key={k}
              fieldKey={k}
              value={v}
              path={[...path, k]}
              onChange={onChange}
              search={childSearch}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Boolean → Switch ─────────────────────────────────────────────────────
  if (typeof value === "boolean") {
    return (
      <div style={{ marginBottom: "10px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="body2" style={{ color: "#ddd" }}>
          {label}
        </Typography>
        <Switch
          size="small"
          checked={value}
          onChange={(e) => onChange(path, e.target.checked)}
          color="primary"
        />
      </div>
    );
  }

  // ── Slider ───────────────────────────────────────────────────────────────
  if (hint?.type === "slider") {
    return (
      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
          <Typography variant="body2" style={{ color: "#ddd" }}>
            {label}
          </Typography>
          <Typography variant="caption" style={{ color: "#aaa", minWidth: "36px", textAlign: "right" }}>
            {typeof value === "number" ? Math.round(value * 100) + "%" : ""}
          </Typography>
        </div>
        <Slider
          value={typeof value === "number" ? value : 1}
          min={hint.min ?? 0}
          max={hint.max ?? 1}
          step={hint.step ?? 0.01}
          onChange={(_e, newValue) => onChange(path, newValue)}
          style={{ color: "#90caf9" }}
        />
      </div>
    );
  }

  // ── Enum string → Select ─────────────────────────────────────────────────
  if (hint?.type === "select") {
    return (
      <div style={{ marginBottom: "10px" }}>
        <Typography variant="caption" style={{ color: "#aaa", display: "block", marginBottom: "4px" }}>
          {label}
        </Typography>
        <Select
          fullWidth
          value={value ?? ""}
          onChange={(e) => onChange(path, e.target.value)}
          style={{ backgroundColor: FIELD_BG, color: "#fff" }}
        >
          {(hint.options || []).map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </div>
    );
  }

  // ── Number → number TextField ────────────────────────────────────────────
  if (typeof value === "number") {
    return (
      <div style={{ marginBottom: "10px" }}>
        <TextField
          fullWidth
          label={label}
          type="number"
          size="small"
          value={value}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            onChange(path, isNaN(parsed) ? 0 : parsed);
          }}
          InputProps={{ style: { color: "#fff", backgroundColor: FIELD_BG } }}
          InputLabelProps={{ style: { color: "#aaa" } }}
          variant="outlined"
        />
      </div>
    );
  }

  // ── String → text TextField (fallback) ──────────────────────────────────
  return (
    <div style={{ marginBottom: "10px" }}>
      <TextField
        fullWidth
        label={label}
        size="small"
        value={value ?? ""}
        onChange={(e) => onChange(path, e.target.value)}
        InputProps={{ style: { color: "#fff", backgroundColor: FIELD_BG } }}
        InputLabelProps={{ style: { color: "#aaa" } }}
        variant="outlined"
      />
    </div>
  );
}

// ─── Canvas section ───────────────────────────────────────────────────────────
/**
 * The snapshot's design space, given a section of its own rather than a branch
 * of the generic settings tree.
 *
 * BASE DIMENSIONS ARE STAGED, NOT LIVE. Every other field in this panel previews
 * as you type, but the base dimensions redefine the design space: component
 * positions are 0..1 ratios so they follow the new frame, while their sizes are
 * design pixels and do NOT — so authored content generally needs a fresh start,
 * and any `scripts[]` game carries its own copy of the old space and is never
 * told the space moved. Typing "414" one keystroke at a time would also reflow
 * the whole scene at 4, then 41, then 414. So the numbers are edited locally and
 * committed by APPLY, which persists them and restarts the preview iframe — the
 * same reload path the agent uses (`playengine:agent-reload`).
 *
 * The debug toggle is the opposite: it only draws an overlay, so it applies live.
 */
function CanvasSection({ canva, canEdit, onApplyBaseDimensions, onToggleDebug, search = "" }) {
  const baseDimensions = _.get(canva, "baseDimensions");
  const [draft, setDraft] = useState(baseDimensions);

  // Re-seed when the panel reloads (version switch, preview reload) — otherwise
  // the section would keep showing the previous snapshot's numbers.
  useEffect(() => {
    setDraft(baseDimensions);
  }, [baseDimensions]);

  if (!draft) return null;

  const dirty = !_.isEqual(draft, baseDimensions);
  const showAll = !search.trim() || matchesTabSearch("canvas", search) || matchesTabSearch("base dimensions", search);
  const showDimensions = showAll || ["portrait", "landscape", "width", "height"].some((name) => matchesTabSearch(name, search));
  const showDebug = showAll || matchesTabSearch("show canvas bounds", search) || matchesTabSearch("debug", search);

  const setDim = (orientation, axis, raw) => {
    const parsed = parseInt(raw, 10);
    setDraft((prev) => ({
      ...prev,
      [orientation]: { ...prev[orientation], [axis]: Number.isNaN(parsed) ? 0 : parsed },
    }));
  };

  const renderPair = (orientation) => (
    (!showAll && !matchesTabSearch(orientation, search) && !["width", "height"].some((axis) => matchesTabSearch(axis, search))) ? null :
    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
      <Typography variant="body2" style={{ color: "#ddd", width: 76 }}>
        {orientation === "portrait" ? "Portrait" : "Landscape"}
      </Typography>
      {["width", "height"].filter((axis) => showAll || matchesTabSearch(orientation, search) || matchesTabSearch(axis, search)).map((axis) => (
        <TextField
          key={axis}
          type="number"
          size="small"
          variant="outlined"
          label={axis}
          disabled={!canEdit}
          value={_.get(draft, [orientation, axis], 0)}
          onChange={(e) => setDim(orientation, axis, e.target.value)}
          InputProps={{ style: { color: "#ddd", background: FIELD_BG } }}
          InputLabelProps={{ style: { color: "#999" } }}
          style={{ width: 110 }}
        />
      ))}
    </div>
  );

  return (
    <Card style={{ backgroundColor: CARD_BG }}>
      <CardContent>
        <Typography variant="subtitle2" style={{ color: "#fff", fontWeight: 600, marginBottom: 4 }}>
          Canvas
        </Typography>
        <Typography variant="caption" style={{ color: "#aaa", display: "block", marginBottom: 12 }}>
          The design space every position (0..1) and size (design px) in this snapshot is measured
          against.
        </Typography>

        {showDimensions && <Typography variant="body2" style={{ color: "#bbb", marginBottom: 8 }}>
          Base dimensions
        </Typography>}
        {showDimensions && renderPair("portrait")}
        {showDimensions && renderPair("landscape")}

        {showDimensions && <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!canEdit || !dirty}
            onClick={() => onApplyBaseDimensions(draft)}
          >
            Apply
          </Button>
          <Typography variant="caption" style={{ color: dirty ? "#ffb300" : "#888" }}>
            {dirty
              ? "Not applied yet — Apply saves and restarts the preview."
              : "Applying restarts the preview so the scene rebuilds in the new space."}
          </Typography>
        </div>}

        {showDebug && <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 16,
            paddingTop: 12,
            borderTop: "1px solid #555",
          }}
        >
          <div>
            <Typography variant="body2" style={{ color: "#ddd" }}>
              Show canvas bounds
            </Typography>
            <Typography variant="caption" style={{ color: "#888" }}>
              Preview only — never drawn in an exported playable.
            </Typography>
          </div>
          <Switch
            size="small"
            color="primary"
            disabled={!canEdit}
            checked={_.get(canva, "debug", false) === true}
            onChange={(e) => onToggleDebug(e.target.checked)}
          />
        </div>}
      </CardContent>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
/**
 * PlayEngine General Settings panel.
 *
 * Automatically adapts to whatever fields are present in the snapshot's
 * `generalSettings` object — no code changes needed when new primitive or
 * object fields are added to GeneralSettings in the PlayEngine.
 * The only exception is enum (string) fields, which need a FIELD_HINTS entry
 * at the top of this file for a proper Select control (will fall back to
 * a text input otherwise).
 *
 * @param props.selectedVersion              {Object}
 * @param props.selectedBase                 {Object}
 * @param props.selectedGame                 {Object}
 * @param props.setSelectedVersionPathValue  {function}
 * @param props.userPermissions              {Object}
 * @param props.createSnackbar               {function}
 * @param props.isPreviewLoaded              {boolean}
 * @param props.iFrameId                     {string}
 */
export default function containerPlayEngineGeneralSettings(props) {
  const [settings, setSettings] = useState(null); // null = loading
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState(null);
  const persistDebounceRef = useRef(null);
  // Ref mirror of `settings`, always updated synchronously alongside setSettings.
  // Lets the debounced sync read the latest full object without stale closures.
  const settingsRef = useRef(null);
  // Cancellation token, matching containerPlayEngineCustomVariables: every load
  // takes a generation, and any continuation that finds a newer one has been
  // superseded — by the next snapshot, or by this panel unmounting.
  const loadGenRef = useRef(0);

  // ── Load settings ─────────────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    const gen = ++loadGenRef.current;
    setLoadError(null);

    // 1. Try to get the live value from the playEngine iframe
    if (props.iFrameId && props.isPreviewLoaded) {
      try {
        const resp = await sendCommand(props.iFrameId, {
          type: "playengine:get-general-settings",
        });
        // The round-trip has a timeout of its own, so this can land long after
        // the snapshot it was asked for stopped being the selected one.
        if (gen !== loadGenRef.current) return;
        if (resp?.settings && typeof resp.settings === "object") {
          const withDefaults = withGeneralSettingsDefaults(resp.settings);
          settingsRef.current = withDefaults;
          setSettings(withDefaults);
          return;
        }
      } catch {
        // fall through to snapshot fallback
        if (gen !== loadGenRef.current) return;
      }
    }

    // 2. Fall back to the persisted snapshot value
    const persisted = _.get(props, "selectedVersion.playEngineConfig.generalSettings");
    if (persisted && typeof persisted === "object") {
      const withDefaults = withGeneralSettingsDefaults(persisted);
      settingsRef.current = withDefaults;
      setSettings(withDefaults);
    } else {
      setLoadError("No general settings found in snapshot.");
    }
  }, [props.iFrameId, props.isPreviewLoaded, props.selectedVersion]);

  useEffect(() => {
    if (!props.selectedVersion) return undefined;
    settingsRef.current = null;
    setSettings(null);
    loadSettings();
    // Unmount retires the generation, so a reply arriving after the panel is
    // gone is discarded rather than setting state on a dead component.
    // Switching snapshots unmounts these panels, which is when that happened.
    return () => {
      loadGenRef.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_.get(props, "selectedVersion._id"), props.isPreviewLoaded]);

  // Clear any pending debounced sync on unmount; flush persist so edits are not lost.
  useEffect(() => {
    return () => {
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current);
        persistDebounceRef.current = null;
        const latest = settingsRef.current;
        if (latest) {
          props.setSelectedVersionPathValue?.({
            path: "playEngineConfig.generalSettings",
            value: latest,
          });
        }
      }
      previewQueue.discard(props.iFrameId);
    };
  }, [props.setSelectedVersionPathValue]);

  /**
   * The stored config was rewritten by something other than this panel — undo /
   * redo in the snapshot editor, which bumps `externalConfigRevision`.
   *
   * This panel edits its own copy of the settings, so a restored value reaches
   * it only by being read back out of the snapshot. The engine is told as well:
   * undo replays component config only, and the preview would otherwise keep
   * running the settings that were just undone.
   */
  useEffect(() => {
    if (!props.externalConfigRevision) return;

    const persisted = _.get(props, "selectedVersion.playEngineConfig.generalSettings");
    if (!persisted || typeof persisted !== "object") return;

    // A queued persist holds the value the undo just took back.
    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
      persistDebounceRef.current = null;
    }
    previewQueue.discard(props.iFrameId);

    const withDefaults = withGeneralSettingsDefaults(persisted);
    settingsRef.current = withDefaults;
    setSettings(withDefaults);

    if (props.iFrameId) {
      sendCommand(props.iFrameId, {
        type: "playengine:set-general-settings",
        settings: withDefaults,
      }).catch(() => {/* iframe may not be ready */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.externalConfigRevision]);

  // ── Canvas section handlers ───────────────────────────────────────────────
  /** Shared guard so both canvas actions refuse the same way the fields do. */
  const refuseWithoutPermission = useCallback(() => {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return true;
    }
    return false;
  }, [props.userPermissions, props.createSnackbar]);

  /** Write a patched settings object into state + the ref the debounces read. */
  const commitSettings = useCallback((next) => {
    settingsRef.current = next;
    setSettings(next);
  }, []);

  /**
   * Persist the staged base dimensions and restart the preview.
   *
   * Restart rather than a live `set-general-settings`: the design space moved,
   * so the scene has to be rebuilt in it — and the reload path re-delivers the
   * whole snapshot from the version we just persisted, which is exactly the
   * clean slate a space change needs.
   */
  const applyCanvaBaseDimensions = useCallback((baseDimensions) => {
    if (refuseWithoutPermission()) return;

    // A queued persist/preview would otherwise land after the reload carrying
    // the pre-Apply value.
    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
      persistDebounceRef.current = null;
    }
    previewQueue.discard(props.iFrameId);

    const base = settingsRef.current ?? settings;
    const next = _.cloneDeep(base);
    _.set(next, ["canva", "baseDimensions"], baseDimensions);
    commitSettings(next);

    props.setSelectedVersionPathValue?.({
      path: "playEngineConfig.generalSettings",
      value: next,
    });

    // Same event the agent uses: remounts the iframe, and the ready handshake
    // re-sends the snapshot (containerGamePreview.reloadPlayEngine).
    window.dispatchEvent(new Event("playengine:agent-reload"));
    props.createSnackbar?.("Canvas applied — restarting preview.", "info", false);
  }, [settings, commitSettings, refuseWithoutPermission, props]);

  /** The bounds overlay is a view toggle: apply it live, no reload. */
  const setCanvaDebug = useCallback((value) => {
    if (refuseWithoutPermission()) return;

    const base = settingsRef.current ?? settings;
    const next = _.cloneDeep(base);
    _.set(next, ["canva", "debug"], value);
    commitSettings(next);

    props.setSelectedVersionPathValue?.({
      path: "playEngineConfig.generalSettings",
      value: next,
    });

    if (props.iFrameId) {
      sendCommand(props.iFrameId, {
        type: "playengine:set-general-settings",
        settings: next,
      }).catch(() => {/* iframe may not be ready */ });
    }
  }, [settings, commitSettings, refuseWithoutPermission, props]);

  // ── Apply a field change ──────────────────────────────────────────────────
  const handleChange = useCallback((path, newValue) => {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }

    // Update local state SYNCHRONOUSLY (and the ref mirror) so the field
    // reflects the keystroke immediately and is never clobbered.
    const base = settingsRef.current ?? settings;
    const next = _.cloneDeep(base);
    _.set(next, path, newValue);
    settingsRef.current = next;
    setSettings(next);

    const continuousPreview = isContinuousPreview(path, newValue);

    // Debounced persist — reads settingsRef for the latest full object.
    if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    persistDebounceRef.current = setTimeout(() => {
      persistDebounceRef.current = null;
      const latest = settingsRef.current;
      if (!latest) return;
      props.setSelectedVersionPathValue?.({
        path: "playEngineConfig.generalSettings",
        value: latest,
      });
    }, PERSIST_DEBOUNCE_MS);

    // Live preview. The panel used to debounce this on the same 200 ms as the
    // persist — too slow to feel live, and it conflated a rendering concern with
    // a durability one. The queue coalesces on the next frame instead, so a
    // slider drag costs one message per frame and a toggle still lands at once.
    if (props.iFrameId) {
      previewQueue.push(props.iFrameId, previewQueue.previewKey.generalSettings(), {
        type: "playengine:set-general-settings",
        settings: settingsRef.current,
      });
      if (!continuousPreview) previewQueue.flush(props.iFrameId);
    }
  }, [props.iFrameId, props.userPermissions, props.setSelectedVersionPathValue, settings]);

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (_.isNil(props.selectedVersion) || _.isNil(props.selectedBase)) return null;

  const canvasSearchMatches = !search.trim() ||
    ["canvas", "base dimensions", "portrait", "landscape", "width", "height", "debug", "show canvas bounds"].some((name) => matchesTabSearch(name, search));
  const settingsSearchMatches = settings !== null && Object.entries(settings).some(([key, value]) =>
    !HIDDEN_FIELDS.has(key) && !OWN_SECTION_FIELDS.has(key) && settingMatches(key, value, search)
  );

  return (
    <div style={{ padding: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
      <PlayEngineTabSearch value={search} onChange={setSearch} />

      {/* Header */}
      {!search.trim() && <Card style={{ backgroundColor: CARD_BG }}>
        <CardContent style={{ paddingBottom: "12px" }}>
          <Typography variant="subtitle1" style={{ color: "#fff", fontWeight: 600 }}>
            General Settings
          </Typography>
          <Typography variant="caption" style={{ color: "#aaa" }}>
            {_.get(props, "selectedGame.title", "—")}
          </Typography>
        </CardContent>
      </Card>}

      {/* Game flow diagram (agent-authored, dashboardMeta.gameFlow) */}
      {!search.trim() && <Card style={{ backgroundColor: CARD_BG }}>
        <CardContent>
          <Typography
            variant="subtitle2"
            style={{ color: "#fff", fontWeight: 600, marginBottom: 8 }}
          >
            Game Flow
          </Typography>
          <GameFlowDiagram gameFlow={_.get(props, "selectedVersion.dashboardMeta.gameFlow")} />
        </CardContent>
      </Card>}

      {/* Loading */}
      {settings === null && !loadError && (
        <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
          <CircularProgress size={24} />
        </div>
      )}

      {/* Error */}
      {loadError && (
        <Typography variant="body2" style={{ color: "#f44336", padding: "8px" }}>
          {loadError}
        </Typography>
      )}

      {/* Dynamic fields */}
      {settings !== null && !loadError && settingsSearchMatches && (
        <Card style={{ backgroundColor: CARD_BG }}>
          <CardContent>
            {Object.entries(settings)
              .filter(([key]) => !HIDDEN_FIELDS.has(key) && !OWN_SECTION_FIELDS.has(key))
              .map(([key, value]) => (
                <SettingsField
                  key={key}
                  fieldKey={key}
                  value={value}
                  path={[key]}
                  onChange={handleChange}
                  search={search}
                />
              ))}
          </CardContent>
        </Card>
      )}

      {/* Canvas — its own section, deliberately not part of the generic tree */}
      {settings !== null && !loadError && canvasSearchMatches && (
        <CanvasSection
          canva={settings.canva}
          canEdit={props.userPermissions?.update !== false}
          onApplyBaseDimensions={applyCanvaBaseDimensions}
          onToggleDebug={setCanvaDebug}
          search={search}
        />
      )}

      {settings !== null && !loadError && search.trim() && !settingsSearchMatches && !canvasSearchMatches && (
        <Typography style={{ color: "#888", padding: 8 }}>No field names match this search.</Typography>
      )}

    </div>
  );
}
